import {useState, useRef, useEffect, useMemo} from "react";
import { Col, Row, useIsMobile } from "../../components/Flexbox";
import FilterBar from "../../components/FilterBar";
import Text from "../../components/Text";
import { useTheme } from "../../scripts/functions/ThemeProvider";
import FilesContainer from "../../components/FilesContainer";
import SelectMonthContainer from "../../components/SelectMonthContainer";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SelectableContainer from "../../components/SelectableContainer";
import ProgressBar from "../../components/ProgressBar";
import MaterialsRow from "../../components/MaterialsRow";
import ForecatNormalizer from "../../containers/ForecastNormalizer";
import { Button } from "@mui/material";
import DeleteForecast from "../../components/DeleteForecast";
import ChangeForecastName from "../../components/ChangeForecastName";
import { useSaveToStorage, loadFromStorage, SortData } from "../../utils";
import { useAllForecasts, useForecastJSON } from "../../scripts/queries/useForecastQueries";
import { useForecastList } from "../../containers/ForecastListNormalizer";
import { useCapacidadData } from "../../containers/CapacidadNormalizer";
import { aggregateMaterialsRows } from "../../containers/MaterialsAggregation";

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];

const PRODUCTION_AREAS = ['FLEXO', 'OFFSET', 'OTROS'];
const HIDE_SCROLLBAR = 'crear-forecast-no-scrollbar';

const CrearForecast = () => {
    const persisted = loadFromStorage('FORECAST_FILTERS') || {};
    const {theme} = useTheme();
    const isMobile = useIsMobile();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectFilterMonths, setSelectFilterMonths] = useState(persisted.selectFilterMonths || []);
    const [detectedMonths, setDetectedMonths] = useState([]);
    const [progress, setProgress] = useState(0);
    const fileInputRef = useRef(null);
    const [activeOption, setActiveOption] = useState(persisted.activeOption || "Generar Forecast");
    const [confirmAction, setConfirmAction] = useState([false, false, false]);
    const [selectedArea, setSelectedArea] = useState({});
    const [isPopupOpen, setIsPopupOpen] = useState(null);
    const [activeContainer, setActiveContainer] = useState(persisted.activeContainer || "Capacidad");
    const [showMissingModal, setShowMissingModal] = useState(false);
    const normalizerRef = useRef();

    const {data: forecasts = EMPTY_ARRAY, refetch: refetchForecasts} = useAllForecasts();

    const fetchJSON = activeOption !== "Generar Forecast" && activeOption !== "Normalizar"
    const {data: json, refetch: refetchJSON} = useForecastJSON(activeOption?.prefix, activeContainer, fetchJSON);
    useSaveToStorage('FORECAST_FILTERS', {activeOption, activeContainer, selectFilterMonths})

    const currentYear = new Date().getFullYear();

    const handleClick = (text) => { 
        setActiveContainer((last) => (last === text ? null : text))
    };

    const handleFileChange = (event) => {
        const uniqueItems = new Map([...selectedFiles, ...Array.from(event.target.files)].map(file => [`${file.name}-${file.size}-${file.type}`, file]));
        const uniqueList = Array.from(uniqueItems.values());
        setSelectedFiles(uniqueList); // Store the selected files as an array
        event.target.value = ""
    };
    const [generandoReport, setGenerandoReport] = useState(false);

    const forecastList = useForecastList(forecasts);

    useEffect(() => {
        if (activeOption !== "Generar Forecast" && activeOption !== "Normalizar" && (activeContainer === "Materiales" || activeContainer === "Capacidad")) {
            // console.log("Fore ID:", activeOption);
            // console.log("Key is: ", activeContainer)
            refetchJSON(activeOption.prefix, activeContainer, true);
        }
    }, [activeOption, activeContainer])

    const {filterMonths, getPercentages} = useCapacidadData({json, activeOption, activeContainer, selectFilterMonths, selectedArea, setDetectedMonths});

    const materialsRows = (json && json.length > 0) ? json.filter(item => {
        const hasForecast = item.detalles?.some(detalle => detalle.forecast > 0);
        const hasNoParte = item.partes && item.partes.trim() !== '';

        return hasForecast && hasNoParte;
    }) : EMPTY_ARRAY;

    const aggregatedRows = useMemo(() => aggregateMaterialsRows(materialsRows, selectFilterMonths), [materialsRows, selectFilterMonths]);

    const {sortedItems: sortedMaterialsRows, requestSort, getIcon} = SortData(aggregatedRows, {key: 'forecast', direction: 'descending'});

    const removeFile = (index) => {
        const files = [...selectedFiles];
        files.splice(index, 1);
        setSelectedFiles(files);
    }

    const handleAreaChange = (nombre, area) => {
        setSelectedArea(prev => ({
            ...prev, [nombre]: area
        }))
    }

    const resetStates = () => {
        setSelectedFiles([]);
        setSelectedMonths([]);
        setDetectedMonths([]);
        setProgress(0);
        setConfirmAction([false, false, false]);
        setGenerandoReport(false);
    }

    const redirectToChart = async () => {  //Redirects the user to the charts after the Forecast is generated
        const list = await refetchForecasts();
        if (list.length > 0) {
            setActiveOption(list[0]);
            setActiveContainer("Capacidad");
            resetStates();
        }        
    }

    const handleConfirm = (index) => {
        const newConfirm = [...confirmAction];
        newConfirm[index] = true;
        setConfirmAction(newConfirm);
    }

    const handleDetectedMonths = (months) => {
        setDetectedMonths(months);
    };

    const resetMonths = () => {
        setDetectedMonths([]);
        // setSelectedMonths([]);
    }

    useEffect(() => {
        if(selectedMonths.length > 0) {
            handleConfirm(2);
        }
    }, [selectedMonths])

    const handleGenerateForecast = (selectedMonths) => {
        if(generandoReport) {
            return;
        }
        setGenerandoReport(true);
        if(normalizerRef.current) {
            normalizerRef.current.generateFPR(selectedMonths);
        }
    }

    const openPopup = (popup) => {
        setIsPopupOpen(popup);
    }

    const closePopup = () => {
        setIsPopupOpen(null);
    }

    const renderMissingModal = () => {
        const missingData = json?.find(item => item.isMissing);
        // console.log("Missing data: ", missingData);

        if (!missingData || !showMissingModal) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,  background: 'rgba(0, 0, 0, 0.7)', display: 'flex',  alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={() => setShowMissingModal(false)}>
            <Col style={{ background: theme.Checo, padding: 32, borderRadius: 8, maxWidth: 500, maxHeight: '80vh', border: `1px solid ${theme.Coco}`}} onClick={e => e.stopPropagation()}>
                <Text color={theme.Magenta} weight="bold" size={18}>Números de parte no encontrados</Text>
                <Row style={{width: '100%', alignItems: 'flex-start', height: 28, paddingTop: 25, flexShrink: 0}}>
                    <Text weight="bold" size={15} color={theme.ONED} style={{flex: 0.28, height: 20}}>No. parte</Text>
                    <Text weight="bold" size={15} color={theme.ONED} style={{flex: 0.7, height: 20, textAlign: 'left'}}>Cliente</Text>
                </Row>
                <Col style={{width: '100%', gap: 4, overflowY: 'auto', flex: 1}}>
                    {missingData.missingList?.map((item) => (
                        <Row key={`${item.NoParte}-${item.Cliente}`} style={{width: '100%', padding: 4, marginTop: 10}}>
                            <Text color={theme.ONED} size={14} style={{flex: 0.3, height: 20, textAlign: 'left'}}>{item.NoParte}</Text>
                            <Text color={theme.ONED} size={13} style={{flex: 0.7, height: 20, textAlign: 'left'}}>{item.Cliente}</Text>
                        </Row>
                    ))}
                </Col>
                <Button onClick={() => setShowMissingModal(false)} style={{ marginTop: 20, color: theme.Magenta, fontSize: 15 }}>Cerrar</Button>
            </Col>
        </div>
    );
};

    return (
        <Row className={HIDE_SCROLLBAR} style={{ width:'100%', flex:'none',alignItems:'flex-start', alignContent: 'flex-start', paddingLeft: isMobile ? 16 : 64, paddingRight: isMobile ? 16 : 64, height: isMobile ? 'auto' : '100%', minHeight: isMobile ? '100%' : undefined, overflowY: 'auto', flexWrap: isMobile ? 'wrap' : 'nowrap'}}>
            <style>{`.${HIDE_SCROLLBAR}::-webkit-scrollbar { display: none; }`}</style>
            {isMobile ? (
                <FilterBar style={{marginTop: 32, width: '100%', gap: 12}}>
                    <Col onClick={() => setActiveOption("Generar Forecast")} style={{cursor:'pointer', backgroundColor: activeOption == "Generar Forecast" ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption == "Generar Forecast" ? theme.Magenta : theme.Coco, width: 240, height: 130, flex:'none', borderRadius:4, alignItems:'flex-start', justifyContent: 'center', padding:16}}>
                        <Text color={activeOption == "Generar Forecast" ? theme.Magenta : theme.ONED}>Generar Forecast</Text>
                    </Col>

                    {forecastList.length > 0 && forecastList.slice().map((forecast, index) => {
                        const isActive = activeOption.prefix === forecast?.id?.prefix;
                        return (
                        <Col key={forecast?.id?.prefix ?? index} onClick={() => {setActiveOption({...forecast.id, displayName: forecast.displayName}); setActiveContainer("Capacidad")}} style={{cursor:'pointer', backgroundColor: isActive ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: isActive ? theme.Magenta : theme.Coco, width: 240, height: 130, flex:'none', borderRadius:4, alignItems:'flex-start', padding:16}}>
                            <Row style={{alignItems: 'center', width: '100%', height: '100%'}}>
                                <Col style={{justifyContent:'center', width: '100%', height: '100%', alignItems: 'start', overflow: 'hidden', gap: '4px'}}>
                                    <Text color={isActive ? theme.Magenta : theme.ONED} weight='extrabold' style={{maxWidth: '183px'}}>{forecastList[index] ? forecastList[index].displayName : "Cargando..."}</Text>
                                    <Text color={isActive ? theme.ONED : theme.Audi} size={15} style={{maxWidth: '183px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis'}}>{forecast?.customers}</Text>
                                    <Text color={isActive ? theme.ONED : theme.Audi} size={11} style={{display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '183px'}}>{forecast?.date}</Text>
                                </Col>
                                <ArrowForwardIosIcon style={{fontSize: 16, color: isActive ? theme.Magenta : theme.Coco}}/>
                            </Row>
                        </Col>
                        );
                    })}
                </FilterBar>
            ) : (
            <Col style={{marginTop:32, height: '75%', overflowY: 'auto', justifyContent: 'start', maxWidth: 380, flex: 'none', scrollbarWidth: 'none', flexShrink: 0, position: 'sticky', top: 32}}>
                <Col onClick={() => setActiveOption("Generar Forecast")} style={{cursor:'pointer', backgroundColor: activeOption == "Generar Forecast" ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption == "Generar Forecast" ? theme.Magenta : theme.Coco, width:'100%', height:52, flex:'none', borderRadius:4, alignItems:'flex-start', padding:16}}>
                    <Text color={activeOption == "Generar Forecast" ? theme.Magenta : theme.ONED}>Generar Forecast</Text>
                </Col>

                {forecastList.length > 0 && forecastList.slice().map((forecast, index) => {
                    const isActive = activeOption.prefix === forecast?.id?.prefix;
                    return (
                    <Col key={forecast?.id?.prefix ?? index} onClick={() => {setActiveOption({...forecast.id, displayName: forecast.displayName}); setActiveContainer("Capacidad")}} style={{cursor:'pointer', marginTop:16, backgroundColor: isActive ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: isActive ? theme.Magenta : theme.Coco, width:'100%', minHeight:52, flex:'none', borderRadius:4, alignItems:'flex-start', padding:16}}>
                        <Row style={{alignItems: 'center', width: '100%'}}>
                            <Col style={{justifyContent:'space-between', width: '100%', alignItems: 'start', overflow: 'hidden', textOverFlow: 'ellipsis', whiteSpace: 'pre-line', overflowWrap: 'break-word', gap: '4px'}}>
                                <Text color={isActive ? theme.Magenta : theme.ONED} weight='extrabold'>{forecastList[index] ? forecastList[index].displayName : "Cargando..."}</Text>
                                <Text color={isActive ? theme.ONED : theme.Audi} size={15} style={{maxWidth: '285px'}}>{forecast?.customers}</Text>
                                <Text color={isActive ? theme.ONED : theme.Audi} size={11}>{forecast?.date}</Text>
                            </Col>
                            <ArrowForwardIosIcon style={{fontSize: 16, color: isActive ? theme.Magenta : theme.Coco}}/>
                        </Row>
                    </Col>
                    );
                })}
            </Col>
            )}
            {activeOption == "Generar Forecast" ? (<Col style={{flex: isMobile ? 'none' : 3, width: isMobile ? '100%' : undefined, height: isMobile ? 'auto' : undefined, alignItems:'flex-start', justifyContent: 'flex-start', alignSelf: isMobile ? 'flex-start' : undefined, paddingLeft: isMobile ? 0 : 32, paddingTop: 32, paddingBottom: 32}}>
                <Col style={{ flex:'none', paddingLeft: 1, width:'100%', alignItems:'flex-start', justifyContent: 'flex-start', height: '100%'}}>
                    <Col style={{width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start'}}>  {/* This Col is required for the scroll to work in all the space */}
                    <Text color={theme.ONED} weight={"medium"} size={isMobile ? 16 : 20}>1. Carga los archivos de forecast enviados por el cliente.</Text>
                    <FilesContainer removeFile={removeFile} opacity={selectedFiles.length > 0 ? 1 : 0} theme={theme} files={selectedFiles}/>
                    <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} onClick={() => {fileInputRef.current.click();}} color={theme.Magenta}  size={15}>Cargar archivos</Text>
                    <input style={{display:'none'}}ref={fileInputRef} type="file" onChange={(e) => handleFileChange(e)} multiple accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"/>
                    <Button  disabledElevation disabled={selectedFiles.length > 0 ? false : true} variant="contained" onClick={() => handleConfirm(0)} sx={{width: isMobile ? '70%' : 342, maxWidth: 342, height: 52, backgroundColor: theme.ONED, color: theme.White, fontWeight: 'medium', fontSize: 15, textTransform: 'none', marginTop: 4}}>Confirmar archivos</Button>
                    {confirmAction[0] && (<Col style={{alignItems:'flex-start'}}><Text color={theme.ONED} weight={"medium"} size={isMobile ? 16 : 20} style={{marginTop:24}}>2. Limpia y confirma las tablas a utilizar de cada archivo</Text>
                    <Col style={{alignItems: 'flex-start', paddingTop: 20, gap: 8}}>
                        <Text color={theme.Lime}>Verde = Número de parte</Text>
                        <Text color={theme.Orange}>Naranja = Mes {currentYear}</Text>
                        <Text color={theme.Brown}>Café = Mes {currentYear + 1}</Text>
                        <Text color={theme.Blue}>Azul = Semana {currentYear}</Text>
                        <Text color={theme.Teal}>Turquesa = Semana {currentYear + 1}</Text>
                        <Text color={theme.Lao}>Gris = Columnas no utilizadas</Text>
                    </Col>
                    <Row style={{paddingTop: 32, paddingBottom: 32}}> <ForecatNormalizer theme={theme} selectedFiles={selectedFiles} removeFile={removeFile} redirectToChart={redirectToChart} confirmAction={confirmAction} handleConfirm={handleConfirm} detectMonths={handleDetectedMonths} resetMonths={resetMonths} ref={normalizerRef} generateReport={setGenerandoReport} addProgress={setProgress} progress={progress}/> </Row></Col>)}
                    {confirmAction[1] && (<Col style={{alignItems:'flex-start'}}>
                    <Text color={theme.ONED} weight={"medium"} size={isMobile ? 16 : 20} style={{marginTop:24}}>3. Selecciona los meses a incluir en el reporte.</Text>
                    <SelectMonthContainer showYear={true} selectedMonths={selectedMonths} updateMonths={setSelectedMonths} detectedMonths={detectedMonths} theme={theme}/>
                    {confirmAction[2] && (<Col style={{ marginTop:16, alignItems: 'flex-start', gap: 16, width: isMobile ? '100%' : undefined}}>
                        <Text color={theme.ONED} weight={"medium"} size={isMobile ? 16 : 20} style={{marginTop:24}}>4. Genera el reporte.</Text>
                        <Button  disabledElevation disabled={selectedMonths.length === 0 || generandoReport} variant="contained" onClick={() => handleGenerateForecast(selectedMonths)} sx={{width: isMobile ? '70%' : 342, maxWidth: 342, height: 52, backgroundColor: theme.ONED, color: theme.White, fontWeight: 'medium', fontSize: 15, textTransform: 'none', marginBottom: 2}}>Generar reporte</Button>
                        {generandoReport && (<Col style={{backgroundColor: theme.Coco, flex:'none', width: isMobile ? '70%' : 316, maxWidth: 316, height: 5, borderRadius: 10, alignItems:"flex-start"}}>
                            <Col className={'div-animate'} style={{width:`${progress}%`, borderRadius:10}} ></Col>
                        </Col>)}
                    </Col>)}</Col>)}
                    </Col>
                </Col>
            </Col>
            ):(
            <Col style={{width: '100%', alignItems:'flex-start', paddingLeft: isMobile ? 0 : 20, paddingTop: 25, paddingBottom: 32}}>
                <Col style={{width:'100%', alignItems:'flex-start', justifyContent: 'flex-start'}}>
                    <FilterBar style={{width: '100%', height: '40px', justifyContent: 'start', paddingTop: '10px', paddingBottom: '5px', gap: '16px'}}>
                        <ChangeForecastName theme={theme} forecast={activeOption} refresh={refetchForecasts}/>
                        <Text style={{ cursor:'pointer', transition:'transform 2s ease', paddingLeft: '16px'}} onClick={() => openPopup(true)} color={theme.Magenta} size={15}>Eliminar</Text>
                        {isPopupOpen && <DeleteForecast theme={theme} forecast={activeOption.prefix} refresh={refetchForecasts} option={setActiveOption} onClose={closePopup}/>}
                        {activeContainer === "Materiales" && json && (
                            <> 
                                {(() => {
                                    const count = json.find(i => i.isMissing)?.missingList?.length;
                                    return count > 0 && (
                                        <Text style={{flex: 1, cursor:'pointer', transition:'transform 2s ease', textAlign: 'right'}} onClick={() => setShowMissingModal(true)} color={theme.Red}>{count} números de parte no encontrados</Text>
                                    )
                                })()}
                            </>
                        )}
                    </FilterBar>
                    <FilterBar style={{width: '100%', justifyContent: 'space-between', alignItems: 'flex-end'}}>
                        <Row style={{minWidth: '70%', wrap: 'nowrap'}}>
                            <SelectMonthContainer style={{flexWrap: 'nowrap', width: '100%'}} showYear={false} selectedMonths={selectFilterMonths} updateMonths={setSelectFilterMonths} detectedMonths={detectedMonths} theme={theme}/>
                        </Row>
                        <Row style={{justifyContent: 'flex-end', maxWidth: 300}}>
                            <SelectableContainer theme={theme} text="Capacidad" active = {activeContainer === "Capacidad"} onClick = {() => handleClick("Capacidad")}></SelectableContainer>
                            <SelectableContainer theme={theme} text="Materiales" active = {activeContainer === "Materiales"} onClick = {() => handleClick("Materiales")}></SelectableContainer>
                        </Row>
                    </FilterBar>
                    {activeContainer === "Capacidad" && (
                         <Col style={{ paddingTop: 40, width: '100%', height: '100%'}}>
                            <div className={isMobile ? HIDE_SCROLLBAR : undefined} style={{width: '100%', overflowX: isMobile ? 'auto' : 'hidden'}}>
                                <Row style={{ justifyContent: 'space-between', width: '100%', minWidth: isMobile ? 948 : undefined, alignItems: 'flex-start' }}>
                                    {PRODUCTION_AREAS.map((area) => (
                                        <Col key={area} style={{ flex: 1, flexShrink: 0, alignItems: 'start', gap: '10px'}}>
                                            <Text weight="bold" size={15} color={theme.ONED} style={{paddingBottom: 15}}>{area}</Text>
                                            {filterMonths.filter(f => f.area.startsWith(area)).map((f) => (
                                                <ProgressBar theme={theme} key={f.nombre} data={f} forecast={json} onAreaChange={handleAreaChange} onPercentageChange={getPercentages} selectedArea={selectedArea[f.nombre || f.area]}/>
                                            ))}
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </Col>
                    )}
                    {(activeContainer === "Materiales" &&
                        <Col style={{width: '100%', alignItems: 'flex-start', justifyContent: 'flex-start', marginTop: 20}}>
                            <div className={isMobile ? HIDE_SCROLLBAR : undefined} style={{width: '100%', overflowX: isMobile ? 'auto' : 'hidden'}}>
                                <Col style={{width: '100%', minWidth: isMobile ? 929 : undefined, alignItems: 'flex-start'}}>
                                    <Row style={{ justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', height: 28}}>
                                        <Text onClick={() => requestSort('codItem')} weight="medium" size={15} color={theme.ONED} style={{width: 120, height: 20, cursor: 'pointer'}}>Material {getIcon('codItem')}</Text>
                                        <Text onClick={() => requestSort('material')} weight="medium" size={15} color={theme.ONED} style={{width: 319, height: 20, cursor: 'pointer'}}>Descripción {getIcon('material')}</Text>
                                        <Text onClick={() => requestSort('_customers')} weight="medium" size={15} color={theme.ONED} style={{width: 120, height: 20, cursor: 'pointer'}}>Cliente {getIcon('_customers')}</Text>
                                        <Text onClick={() => requestSort('_parts')} weight="medium" size={15} color={theme.ONED} style={{width: 181, height: 20, cursor: 'pointer'}}>No. Parte {getIcon('_parts')}</Text>
                                        <Text onClick={() => requestSort('_forecast')} weight="medium" size={15} color={theme.ONED} style={{width: 120, height: 20, textAlign: 'right', cursor: 'pointer'}}>Forecast {getIcon('_forecast')}</Text>
                                        <Text onClick={() => requestSort('unidad')} weight="medium" size={15} color={theme.ONED} style={{width: 69, height: 20, textAlign: 'right', cursor: 'pointer'}}>Unidad {getIcon('unidad')}</Text>
                                    </Row>
                                    <div style={{width: '100%', marginTop: 20}}>
                                        <Col style={{width: '100%', flex: 'none'}}>
                                            {sortedMaterialsRows.map(item => {
                                                return (
                                                    <MaterialsRow selectedMonths={selectFilterMonths} theme={theme} key={item.id} data={item} style={{justifyContent: 'space-between'}}/>
                                                )
                                            })}
                                        </Col>
                                    </div>
                                </Col>
                            </div>
                        </Col> //Avoid errors if the json is null
                    )}
                </Col>
            </Col>
            )}
            {renderMissingModal()}
        </Row>
    )
}

export default CrearForecast;